import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Notification as NotificationComponent,
  NotificationProps,
} from '@app/components';
import { generateUniqueId } from '@app/helpers';
import { Card } from 'components/ui/card';

interface Props extends PropsWithChildren {}

interface NotificationPickedProps
  extends Pick<NotificationProps, 'variant' | 'message'> {}

export interface NotificationItem extends NotificationPickedProps {
  id: string;
}

interface NotificationContextValue {
  showNotification: (notification: NotificationPickedProps) => void;
}

const NotificationContext = createContext<NotificationContextValue>(
  {} as NotificationContextValue
);

const notificationsRoot = document.getElementById('notifications-root');

function Notification(
  props: NotificationItem & {
    hideNotification: (id: string) => void;
  }
) {
  const { id, variant, message, hideNotification } = props;
  const [focused, setFocused] = useState<boolean>(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const onMouseEnter = useCallback(() => {
    setFocused(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setFocused(false);
  }, []);

  useEffect(() => {
    if (focused) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    } else {
      timeout.current = setTimeout(() => {
        hideNotification(id);
      }, 3000);
    }
  }, [focused, hideNotification, id]);

  // Добавляем зелёный фон для variant success
  const cardClass =
    variant === 'success'
      ? 'mb-2 shadow-lg w-full max-w-md bg-green-500 text-white'
      : 'mb-2 shadow-lg w-full max-w-md';

  return (
    <Card
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cardClass}
    >
      <NotificationComponent variant={variant} message={message} />
    </Card>
  );
}

function NotificationProvider(props: Props) {
  const { children } = props;
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const el = useMemo<HTMLDivElement>(() => {
    const divElement = document.createElement('div');
    divElement.classList.add('b-notifications');
    return divElement;
  }, []);

  const showNotification = useCallback(
    (notification: NotificationPickedProps) => {
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        {
          ...notification,
          id: generateUniqueId(),
        },
      ]);
    },
    []
  );

  const hideNotification = useCallback((id: string) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((prevNotification) => prevNotification.id !== id)
    );
  }, []);

  const renderNotifications = useCallback(() => {
    return notifications.map((notification) => (
      <Notification
        key={notification.id}
        {...notification}
        hideNotification={hideNotification}
      />
    ));
  }, [hideNotification, notifications]);

  useEffect(() => {
    notificationsRoot?.appendChild(el);
    return () => {
      notificationsRoot?.removeChild(el);
    };
  }, [el]);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
      }}
    >
      {children}
      {createPortal(renderNotifications(), el)}
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  return useContext(NotificationContext);
}

export default NotificationProvider;
